/**
 * Media Showcase: scroll-linked scale via Motion (288-media-showcase).
 * Easing follows the global "Appear" curve (Settings › Animations › Appear),
 * shared with 288-stagger-appear.js via window.__288Stagger.ease.
 */
const ROOT_SELECTOR = "[data-media-showcase-scroll]";

/**
 * Easing presets, mirrored from 288-stagger-appear.js so the media scale-in uses
 * the same configurable curve.
 * @type {Record<string, number[] | "linear">}
 */
const EASE_PRESETS = {
  "expo-out": [0.19, 1, 0.22, 1],
  "cubic-out": [0.215, 0.61, 0.355, 1],
  "quad-out": [0.25, 0.46, 0.45, 0.94],
  "sine-out": [0.39, 0.575, 0.565, 1],
  linear: "linear",
};
// Media showcase is scroll-linked, so it follows the "On scroll" curve.
const __staggerEase =
  (typeof window !== "undefined" &&
    window.__288Stagger &&
    window.__288Stagger.easeScroll) ||
  "expo-out";
const EASE_TRANSFORM = EASE_PRESETS[__staggerEase] || EASE_PRESETS["expo-out"];

const cleanups = new WeakMap();

/** @param {() => void} fn */
function whenReady(fn) {
  if (document.readyState !== "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Detect whether the browser is throttling requestAnimationFrame (e.g. iOS Low
 * Power Mode caps rAF — and CSS animations — to ~30fps). Continuous scroll-linked
 * scrubbing looks choppy at that cadence, so callers fall back to a one-shot reveal.
 * Measured once and cached globally so every section shares the same sample.
 * @returns {Promise<{ fps: number, throttled: boolean }>}
 */
function detectFrameBudget() {
  if (window.__288FrameBudgetPromise) return window.__288FrameBudgetPromise;
  window.__288FrameBudgetPromise = new Promise((resolve) => {
    const SAMPLES = 12;
    const WARMUP = 3;
    /** @type {number[]} */
    const deltas = [];
    let last = 0;
    let count = 0;
    const tick = (/** @type {number} */ t) => {
      if (last) deltas.push(t - last);
      last = t;
      if (++count <= SAMPLES) {
        requestAnimationFrame(tick);
        return;
      }
      const usable = deltas.slice(WARMUP).sort((a, b) => a - b);
      const median = usable[Math.floor(usable.length / 2)] || 16;
      // > 26ms ≈ ≤ 38fps ⇒ treat as throttled.
      resolve({ fps: 1000 / median, throttled: median > 26 });
    };
    requestAnimationFrame(tick);
  });
  return window.__288FrameBudgetPromise;
}

/** @param {HTMLElement} root */
async function initRoot(root) {
  if (prefersReducedMotion()) return;
  if (root.dataset.mediaShowcaseBound === "true") return;
  const target = root.querySelector("[data-media-showcase-scroll-target]");
  if (!target) return;

  root.dataset.mediaShowcaseBound = "true";

  let motion;
  try {
    motion = await import("@theme/motion");
  } catch {
    delete root.dataset.mediaShowcaseBound;
    return;
  }

  const { scroll, animate, inView } = motion;

  // When rAF is throttled (iOS Low Power Mode), per-frame scroll scrubbing is
  // capped to ~30fps and looks choppy. Reveal once on enter instead — a short
  // transition reads cleanly even at the throttled cadence.
  const { throttled } = await detectFrameBudget();
  if (root.dataset.mediaShowcaseBound !== "true") return;
  if (throttled) {
    const stop = inView(
      root,
      () => {
        animate(target, { scale: [0.96, 1] }, { duration: 0.6, ease: EASE_TRANSFORM });
      },
      { amount: 0.2 },
    );
    cleanups.set(root, () => {
      stop();
    });
    return;
  }

  // Promote the animated subtree to its own compositor layer so the scroll-linked
  // scale update is composited (GPU) instead of repainting the whole media subtree
  // on every scroll frame — the main cause of jank, including desktop.
  target.style.willChange = "transform";
  target.style.backfaceVisibility = "hidden";

  const scaleAnimation = animate(
    target,
    { scale: [0.92, 1] },
    { ease: EASE_TRANSFORM },
  );

  const cancelScale = scroll(scaleAnimation, {
    target: root,
    offset: ["start end", "center center"],
  });
  cleanups.set(root, () => {
    cancelScale();
    target.style.willChange = "";
    target.style.backfaceVisibility = "";
  });
}

/** @param {HTMLElement} root */
function destroyRoot(root) {
  const cancel = cleanups.get(root);
  if (typeof cancel === "function") cancel();
  cleanups.delete(root);
  delete root.dataset.mediaShowcaseBound;
}

function run() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initRoot(el);
  });
}

whenReady(run);

document.addEventListener("shopify:section:load", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const roots = t.querySelectorAll(ROOT_SELECTOR);
  roots.forEach((el) => {
    if (el instanceof HTMLElement) initRoot(el);
  });
});

document.addEventListener("shopify:section:unload", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const roots = t.querySelectorAll(ROOT_SELECTOR);
  roots.forEach((el) => {
    if (el instanceof HTMLElement) destroyRoot(el);
  });
});

export {};
